/*
 * GegeMoon global front-end behaviour.
 *
 * Nav scroll-blur: on pages where the nav overlays a dark hero (the homepage),
 * the bar starts transparent with light text and switches to a blurred cream
 * bar with dark text once the page is scrolled past the trigger. Solid bars
 * (every other page) opt out via the gm-nav--solid class.
 *
 * The nav element is re-queried inside the handler — rather than captured in a
 * closure — so the behaviour keeps working after the theme editor or
 * `shopify theme dev` hot-reloads the header section and replaces the node.
 */
(function () {
  var SCROLL_TRIGGER = 60;

  function getScrollY() {
    return (
      window.scrollY ||
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0
    );
  }

  function updateNav() {
    var nav = document.getElementById('gm-nav');
    // No nav, or a solid (non-overlay) bar that never changes on scroll.
    if (!nav || nav.classList.contains('gm-nav--solid')) {
      return;
    }
    if (getScrollY() > SCROLL_TRIGGER) {
      nav.classList.add('gm-nav--scrolled');
    } else {
      nav.classList.remove('gm-nav--scrolled');
    }
  }

  window.addEventListener('scroll', updateNav, { passive: true });
  window.addEventListener('resize', updateNav, { passive: true });

  // Re-apply when the theme editor reloads/reorders the header section.
  document.addEventListener('shopify:section:load', updateNav);

  // Set the correct state on initial load (e.g. when the page loads scrolled).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateNav);
  } else {
    updateNav();
  }
})();

/*
 * Mobile nav menu.
 *
 * The hamburger ([data-nav-toggle]) opens/closes the dropdown panel by toggling
 * gm-nav--menu-open on #gm-nav and syncing aria-expanded. Everything is delegated
 * on the document so it survives header hot-reloads. The menu closes on link tap,
 * cart tap, outside tap, Escape, section reload, and once the viewport grows back
 * to desktop (where the panel is just the inline link row again).
 */
(function () {
  function nav() {
    return document.getElementById('gm-nav');
  }

  function isOpen() {
    var el = nav();
    return !!el && el.classList.contains('gm-nav--menu-open');
  }

  function setOpen(open) {
    var el = nav();
    if (!el) {
      return;
    }
    el.classList.toggle('gm-nav--menu-open', open);
    el.querySelectorAll('[data-nav-toggle]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-nav-toggle]')) {
      event.preventDefault();
      setOpen(!isOpen());
      return;
    }
    if (!isOpen()) {
      return;
    }
    // A tap on a panel link, the cart, or anywhere outside the bar dismisses it.
    if (
      event.target.closest('#gm-nav-menu a') ||
      event.target.closest('[data-cart-toggle]') ||
      !event.target.closest('#gm-nav')
    ) {
      setOpen(false);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      setOpen(false);
    }
  });

  window.addEventListener(
    'resize',
    function () {
      if (window.innerWidth > 900) {
        setOpen(false);
      }
    },
    { passive: true }
  );

  document.addEventListener('shopify:section:load', function () {
    setOpen(false);
  });
})();

/*
 * Accordion (product "how to use" panel + FAQ).
 *
 * Faithful port of the mockup's toggleAccordion (_source/product.html):
 * single-open per .accordion group — clicking a header closes every item in the
 * group, then opens the clicked one unless it was already open, rotating its +
 * into an ×. No scroll manipulation: the open/close is left to the CSS
 * max-height transition exactly as the mockup does it. Click is delegated on the
 * document so it works for every accordion and survives section hot-reloads.
 */
(function () {
  document.addEventListener('click', function (event) {
    var header = event.target.closest('.accordion-header');
    if (!header) {
      return;
    }
    var item = header.closest('.accordion-item');
    if (!item) {
      return;
    }
    var group = item.closest('.accordion');
    var wasOpen = item.classList.contains('open');

    if (group) {
      group.querySelectorAll('.accordion-item').forEach(function (other) {
        other.classList.remove('open');
        var otherHeader = other.querySelector('.accordion-header');
        if (otherHeader) {
          otherHeader.setAttribute('aria-expanded', 'false');
        }
      });
    }

    if (!wasOpen) {
      item.classList.add('open');
      header.setAttribute('aria-expanded', 'true');
    }
  });
})();

/*
 * Money formatter — the JS twin of Liquid's `money` filter.
 *
 * Seeded by window.GegeMoon.moneyFormat (= shop.money_format, set in
 * layout/theme.liquid), so quantity totals are formatted identically to
 * server-rendered prices in whatever currency the shop uses — no hardcoded
 * symbol or decimal assumption (CLAUDE.md rule 1). This is the canonical Shopify
 * formatMoney, taking an amount in the currency's minor units (e.g. cents/paisa).
 */
(function () {
  window.GegeMoon = window.GegeMoon || {};

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;

    if (isNaN(number) || number == null) {
      return '0';
    }

    number = (number / 100).toFixed(precision);
    var parts = number.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  }

  window.GegeMoon.formatMoney = function (cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    var formatString = format || window.GegeMoon.moneyFormat || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = formatString.match(placeholder);
    var value = '';

    switch (match ? match[1] : 'amount') {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholder, value);
  };
})();

/*
 * Quantity stepper (product purchase panel).
 *
 * Updates the visible value and the hidden form input, then recomputes the
 * Add-to-Cart line price from data-unit-price (the variant price in minor units)
 * × quantity, formatted through GegeMoon.formatMoney — replacing the mockup's
 * hardcoded `68 * qty`. Clamps to [data-qty-min, data-qty-max] when the variant
 * tracks finite stock. Delegated for hot-reload resilience.
 */
(function () {
  function updateLinePrice(control, qty) {
    var form = control.closest('.gm-product-form');
    if (!form) {
      return;
    }
    var priceEl = form.querySelector('[data-line-price]');
    var unit = parseInt(control.getAttribute('data-unit-price'), 10);
    if (!priceEl || isNaN(unit)) {
      return;
    }
    priceEl.textContent = window.GegeMoon.formatMoney(unit * qty);
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-qty-step]');
    if (!button) {
      return;
    }
    var control = button.closest('[data-qty]');
    if (!control) {
      return;
    }
    var display = control.querySelector('[data-qty-display]');
    var input = control.querySelector('[data-qty-input]');
    var step = parseInt(button.getAttribute('data-qty-step'), 10) || 0;
    var min = parseInt(control.getAttribute('data-qty-min'), 10);
    var max = parseInt(control.getAttribute('data-qty-max'), 10);
    var current = parseInt(display ? display.textContent : '1', 10) || 1;
    var next = current + step;

    if (isNaN(min)) {
      min = 1;
    }
    if (next < min) {
      next = min;
    }
    if (!isNaN(max) && next > max) {
      next = max;
    }

    if (display) {
      display.textContent = next;
    }
    if (input) {
      input.value = next;
    }
    updateLinePrice(control, next);
  });
})();

/*
 * Product image gallery.
 *
 * Clicking a thumbnail swaps the main image. Forward-looking: the store ships
 * with the CSS kit illustration today, and real photography drops into this
 * gallery with no further code. Delegated for hot-reload resilience.
 */
(function () {
  document.addEventListener('click', function (event) {
    var thumb = event.target.closest('[data-gallery-thumb]');
    if (!thumb) {
      return;
    }
    var gallery = thumb.closest('[data-gallery]');
    if (!gallery) {
      return;
    }
    var main = gallery.querySelector('[data-gallery-main]');
    var full = thumb.getAttribute('data-full');
    if (main && full) {
      main.removeAttribute('srcset');
      main.src = full;
    }
    gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (other) {
      other.classList.remove('is-active');
    });
    thumb.classList.add('is-active');
  });
})();

/*
 * Cart — AJAX add-to-cart, buy-now, and the slide-in drawer.
 *
 * Add to Cart / line +/− / Remove all hit the Cart AJAX API and ask it to
 * re-render the `cart-drawer` section; the returned HTML (with fresh,
 * money-filtered prices and subtotal) replaces the drawer's inner block, so no
 * price is ever computed in JS here. Buy Now adds the selection, then hands off
 * to Shopify-hosted checkout. Everything is delegated on the document so it keeps
 * working after section hot-reloads and across the swapped-in drawer markup.
 */
(function () {
  var cfg = window.GegeMoon || {};
  var routes = cfg.routes || {};
  var ADD_URL = routes.cartAdd || '/cart/add';
  var CHANGE_URL = routes.cartChange || '/cart/change';
  var DRAWER_SECTION = 'cart-drawer';
  var busy = false;

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        return { ok: response.ok, data: data };
      });
    });
  }

  function updateBadges(count) {
    var n = parseInt(count, 10) || 0;
    document.querySelectorAll('[data-cart-count-badge]').forEach(function (badge) {
      badge.textContent = n;
      if (n > 0) {
        badge.removeAttribute('hidden');
      } else {
        badge.setAttribute('hidden', '');
      }
    });
  }

  function refreshDrawer(sectionHTML) {
    var current = document.querySelector('[data-cart-drawer]');
    if (!current || !sectionHTML) {
      return;
    }
    var parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
    var newInner = parsed.querySelector('[data-cart-drawer-inner]');
    var newRoot = parsed.querySelector('[data-cart-drawer]');
    var currentInner = current.querySelector('[data-cart-drawer-inner]');

    if (newInner && currentInner) {
      currentInner.innerHTML = newInner.innerHTML;
    }
    if (newRoot) {
      var count = newRoot.getAttribute('data-cart-count');
      current.setAttribute('data-cart-count', count);
      updateBadges(count);
    }
  }

  function openDrawer() {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) {
      return;
    }
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('gm-cart-open');
  }

  function closeDrawer() {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) {
      return;
    }
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('gm-cart-open');
  }

  // Radiate a one-shot gold ring from the cart toggle to confirm an add. Purely
  // decorative (CSS keyframe, disabled under reduced motion); never blocks.
  function pulseCart() {
    document.querySelectorAll('[data-cart-toggle]').forEach(function (el) {
      el.classList.remove('gm-pulse');
      // Force reflow so the ring restarts on rapid re-adds.
      void el.offsetWidth;
      el.classList.add('gm-pulse');
      window.setTimeout(function () {
        el.classList.remove('gm-pulse');
      }, 800);
    });
  }

  function getQuantity(form) {
    var input = form.querySelector('[name="quantity"]');
    return parseInt(input ? input.value : '1', 10) || 1;
  }

  function getVariantId(form) {
    var input = form.querySelector('[name="id"]');
    return input ? input.value : null;
  }

  // Add to Cart — intercept the product form submit, add via AJAX, open drawer.
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('.gm-product-form');
    if (!form) {
      return;
    }
    event.preventDefault();
    if (busy) {
      return;
    }
    var variantId = getVariantId(form);
    if (!variantId) {
      return;
    }
    var addButton = form.querySelector('[data-add-button]');
    busy = true;
    if (addButton) {
      addButton.classList.add('is-loading');
    }

    postJSON(ADD_URL, {
      items: [{ id: variantId, quantity: getQuantity(form) }],
      sections: DRAWER_SECTION,
      sections_url: window.location.pathname
    })
      .then(function (result) {
        busy = false;
        if (addButton) {
          addButton.classList.remove('is-loading');
        }
        if (!result.ok) {
          return;
        }
        if (result.data.sections) {
          refreshDrawer(result.data.sections[DRAWER_SECTION]);
        }
        openDrawer();
        pulseCart();
      })
      .catch(function () {
        busy = false;
        if (addButton) {
          addButton.classList.remove('is-loading');
        }
      });
  });

  // Buy Now — add the current selection, then go straight to checkout.
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-buy-now]');
    if (!button) {
      return;
    }
    event.preventDefault();
    if (busy) {
      return;
    }
    var form = button.closest('.gm-product-form');
    if (!form) {
      return;
    }
    var variantId = getVariantId(form);
    if (!variantId) {
      return;
    }
    busy = true;
    button.classList.add('is-loading');

    postJSON(ADD_URL, { items: [{ id: variantId, quantity: getQuantity(form) }] })
      .then(function (result) {
        if (!result.ok) {
          busy = false;
          button.classList.remove('is-loading');
          return;
        }
        window.location.href = '/checkout';
      })
      .catch(function () {
        busy = false;
        button.classList.remove('is-loading');
      });
  });

  // Change a line's quantity (0 removes it), then re-render the drawer.
  function changeLine(key, quantity) {
    if (busy || !key) {
      return;
    }
    busy = true;
    var drawer = document.querySelector('[data-cart-drawer]');
    if (drawer) {
      drawer.classList.add('is-loading');
    }

    postJSON(CHANGE_URL, {
      id: key,
      quantity: quantity,
      sections: DRAWER_SECTION,
      sections_url: window.location.pathname
    })
      .then(function (result) {
        busy = false;
        if (drawer) {
          drawer.classList.remove('is-loading');
        }
        if (result.ok && result.data.sections) {
          refreshDrawer(result.data.sections[DRAWER_SECTION]);
        }
      })
      .catch(function () {
        busy = false;
        if (drawer) {
          drawer.classList.remove('is-loading');
        }
      });
  }

  // Open / close + in-drawer line controls (all delegated).
  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-cart-toggle]')) {
      event.preventDefault();
      openDrawer();
      return;
    }
    if (event.target.closest('[data-cart-close]') || event.target.closest('[data-cart-overlay]')) {
      closeDrawer();
      return;
    }

    var step = event.target.closest('[data-cart-qty-step]');
    if (step) {
      var control = step.closest('[data-cart-qty]');
      if (!control) {
        return;
      }
      var display = control.querySelector('[data-cart-qty-display]');
      var delta = parseInt(step.getAttribute('data-cart-qty-step'), 10) || 0;
      var current = parseInt(display ? display.textContent : '1', 10) || 1;
      changeLine(control.getAttribute('data-line-key'), Math.max(1, current + delta));
      return;
    }

    var remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      changeLine(remove.getAttribute('data-line-key'), 0);
    }
  });

  // Escape closes the drawer.
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      closeDrawer();
    }
  });
})();

/*
 * Scroll-reveal choreography.
 *
 * Elements marked .gm-reveal fade-and-rise as they enter the viewport and
 * reverse when the user scrolls back up past them. The hidden initial state
 * lives under html.gm-motion, set pre-paint in layout/theme.liquid only when
 * the visitor allows motion — so this is purely additive: under reduced motion
 * or with JS off, content is always visible. One shared IntersectionObserver
 * stays attached permanently so elements animate in both directions, and
 * re-scans on section hot-reload so editor-added content animates too.
 */
(function () {
  var motionEnabled = document.documentElement.classList.contains('gm-motion');

  function revealAll() {
    document.querySelectorAll('.gm-reveal').forEach(function (el) {
      el.classList.add('is-in');
    });
  }

  // Motion off (reduced motion / gate never ran) or no observer support: make
  // sure nothing is left hidden, then stop.
  if (!motionEnabled || !('IntersectionObserver' in window)) {
    revealAll();
    return;
  }

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
        } else if (entry.boundingClientRect.top > 0) {
          // Element exited below the viewport — user scrolled back up past it.
          // Remove is-in so it re-animates on the way back down.
          entry.target.classList.remove('is-in');
        }
        // If top < 0: element exited above the viewport (scrolled down past
        // it). Keep is-in — no reverse animation needed in that direction.
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
  );

  function scan() {
    document.querySelectorAll('.gm-reveal').forEach(function (el) {
      if (el.dataset.gmRevealBound) {
        return;
      }
      el.dataset.gmRevealBound = '1';
      observer.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // Re-scan when the theme editor reloads/reorders a section.
  document.addEventListener('shopify:section:load', scan);
})();

/*
 * Cinematic hero headline.
 *
 * On load (and on editor section reload) the hero <h1> is split into per-word
 * spans that fade-and-rise in sequence, preserving its inline <em> and <br>.
 * Gated by html.gm-motion, so under reduced motion / no-JS the heading is left
 * as plain, fully visible text. Any failure reveals the heading immediately.
 */
(function () {
  function wrapWords(node) {
    Array.prototype.slice.call(node.childNodes).forEach(function (child) {
      if (child.nodeType === 3) {
        var frag = document.createDocumentFragment();
        child.textContent.split(/(\s+)/).forEach(function (part) {
          if (!part) {
            return;
          }
          if (/^\s+$/.test(part)) {
            frag.appendChild(document.createTextNode(part));
          } else {
            var span = document.createElement('span');
            span.className = 'gm-hero__word';
            span.textContent = part;
            frag.appendChild(span);
          }
        });
        node.replaceChild(frag, child);
      } else if (child.nodeType === 1 && child.tagName !== 'BR') {
        wrapWords(child);
      }
    });
  }

  function reveal(heading) {
    if (!heading || heading.dataset.gmWordsBound) {
      return;
    }
    heading.dataset.gmWordsBound = '1';
    if (!document.documentElement.classList.contains('gm-motion')) {
      return;
    }
    try {
      wrapWords(heading);
      heading.querySelectorAll('.gm-hero__word').forEach(function (word, i) {
        word.style.setProperty('--gm-word-delay', 120 + i * 70 + 'ms');
      });
      // Two frames so the hidden state paints before the reveal transition runs.
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          heading.classList.add('is-revealed');
        });
      });
    } catch (e) {
      heading.classList.add('is-revealed');
    }
  }

  function run() {
    reveal(document.querySelector('.gm-hero__heading'));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  document.addEventListener('shopify:section:load', run);
})();

/*
 * Scroll + pointer parallax.
 *
 * Generic, opt-in drift for decorative layers marked [data-gm-parallax] (the
 * attribute value is the scroll speed). [data-gm-parallax-cursor] adds a
 * cursor-follow drift on fine pointers only. Each target receives
 * --gm-parallax-x / --gm-parallax-y, which its CSS composes into a transform —
 * so this layers on top of any existing keyframe animation (e.g. the hero moons'
 * float) instead of fighting it. Gated by html.gm-motion; one rAF-throttled pass
 * per scroll/move. Reused by the hero moons and the Phase E parallax band.
 */
(function () {
  if (!document.documentElement.classList.contains('gm-motion')) {
    return;
  }
  if (!('requestAnimationFrame' in window)) {
    return;
  }
  var fine = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
  var pointerX = 0;
  var pointerY = 0;
  var queued = false;

  function request() {
    if (queued) {
      return;
    }
    queued = true;
    window.requestAnimationFrame(update);
  }

  function update() {
    queued = false;
    var vh = window.innerHeight || 1;
    document.querySelectorAll('[data-gm-parallax]').forEach(function (el) {
      var speed = parseFloat(el.getAttribute('data-gm-parallax')) || 0;
      var rect = el.getBoundingClientRect();
      var fromCenter = rect.top + rect.height / 2 - vh / 2;
      var x = 0;
      var y = -fromCenter * speed;
      if (fine && el.hasAttribute('data-gm-parallax-cursor')) {
        var amt = parseFloat(el.getAttribute('data-gm-parallax-cursor')) || 0;
        x += pointerX * amt;
        y += pointerY * amt;
      }
      el.style.setProperty('--gm-parallax-x', x.toFixed(2) + 'px');
      el.style.setProperty('--gm-parallax-y', y.toFixed(2) + 'px');
    });
  }

  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', request, { passive: true });
  if (fine) {
    window.addEventListener(
      'mousemove',
      function (event) {
        pointerX = event.clientX / (window.innerWidth || 1) - 0.5;
        pointerY = event.clientY / (window.innerHeight || 1) - 0.5;
        request();
      },
      { passive: true }
    );
  }
  document.addEventListener('shopify:section:load', request);
  request();
})();
